import React from "react";
import { View, Text, Image, TouchableOpacity, StyleSheet } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { useTheme } from "../../../context/ThemeContext";

export const Featured = () => {
	const theme = useTheme();
	const styles = useStyles();
	const content = {
		title: "Book of the Month",
		name: "The Psychology of Money",
		subtitle: "Featured!",
		description: "Discover this month's featured book and join the discussion. This month we're exploring 'The Psychology of Money' by Morgan Housel, a fascinating look at how our relationship with money shapes our lives.",
		imageUrl: "https://images.unsplash.com/photo-1544947950-fa07a98d237f?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1287&q=80",
		actionLabel: "View",
		stats: {
			readingCount: 24,
			readingLabel: "reading",
			discussionCount: 12,
			discussionLabel: "messages",
		},
		showStats: true,
		link: "/community/threads/rWwoPItHiOyk",
	};

	const handleNavigation = () => {
		const path = content.link.split("/");
		const linkType = path[2]; // "thread" or "messages"
		const id = path[3]; // the ID number

		// Navigate based on link type
		switch (linkType) {
			case "threads":
				break;
			case "messages":
				break;
		}
	};

	return (
		<View style={styles.container}>
			<View style={styles.header}>
				<View style={styles.headerLeft}>
					<View style={styles.avatarContainer}>
						<Ionicons name="star-outline" size={20} color={theme.colors.primary} />
					</View>
					<Text style={styles.label}>{content.title}</Text>
				</View>
			</View>

			<View style={styles.contentContainer}>
				<View style={styles.imageWrapper}>
					{content.imageUrl ? (
						<Image source={{ uri: content.imageUrl }} style={styles.image} />
					) : (
						<View style={styles.placeholderImage}>
							<Ionicons name="book-outline" size={48} color={theme.colors.textSecondary} />
						</View>
					)}
				</View>

				<View style={styles.details}>
					<Text style={styles.title}>{content.name}</Text>
					<Text style={styles.subtitle}>{content.subtitle}</Text>
					<Text style={styles.description} numberOfLines={3}>
						{content.description}
					</Text>

					{content.showStats !== false && (
						<View style={styles.stats}>
							<View style={styles.statItem}>
								<View style={styles.statIconContainer}>
									<Ionicons name="people-outline" size={18} color={theme.colors.textSecondary} />
								</View>
								<Text style={styles.statText}>
									{content.stats?.readingCount || 24} {content.stats?.readingLabel || "Reading"}
								</Text>
							</View>
							<View style={styles.statItem}>
								<View style={styles.statIconContainer}>
									<Ionicons name="chatbubble-outline" size={18} color={theme.colors.textSecondary} />
								</View>
								<Text style={styles.statText}>
									{content.stats?.discussionCount || 12} {content.stats?.discussionLabel || "Discussions"}
								</Text>
							</View>
						</View>
					)}
				</View>
			</View>

			<View style={styles.footer}>
				<TouchableOpacity style={styles.interactionButton}>
					<Ionicons name="heart-outline" size={24} color={theme.colors.textSecondary} />
				</TouchableOpacity>
				<TouchableOpacity style={styles.interactionButton}>
					<Ionicons name="chatbubble-outline" size={22} color={theme.colors.textSecondary} />
				</TouchableOpacity>

				<TouchableOpacity style={styles.actionButton} onPress={handleNavigation}>
					<Text style={styles.actionButtonText}>{content.actionLabel}</Text>
					<Ionicons name="arrow-forward" size={16} color="#fff" />
				</TouchableOpacity>
			</View>
		</View>
	);
};

const useStyles = () => {
	const theme = useTheme();

	return StyleSheet.create({
		container: {
			backgroundColor: theme.colors.systemBackground.primary,
			borderRadius: 16,
			overflow: "hidden",
			marginBottom: theme.spacing.lg,
			shadowColor: "#000",
			shadowOffset: { width: 0, height: 2 },
			shadowOpacity: 0.1,
			shadowRadius: 8,
			elevation: 3,
		},
		header: {
			flexDirection: "row",
			justifyContent: "space-between",
			alignItems: "center",
			paddingHorizontal: theme.spacing.sm,
			paddingVertical: theme.spacing.sm,
		},
		headerLeft: {
			flexDirection: "row",
			alignItems: "center",
		},
		avatarContainer: {
			width: 32,
			height: 32,
			borderRadius: 16,
			justifyContent: "center",
			alignItems: "center",
			marginRight: theme.spacing.xs,
		},
		label: {
			...theme.typography.headline,
			color: theme.colors.text,
			fontWeight: "600",
			fontSize: 16,
		},
		timestamp: {
			...theme.typography.caption,
			color: theme.colors.textSecondary,
		},
		contentContainer: {
			padding: theme.spacing.sm,
		},
		imageWrapper: {
			width: "100%",
			height: 200,
			borderRadius: 12,
			overflow: "hidden",
			marginBottom: theme.spacing.md,
		},
		image: {
			width: "100%",
			height: "100%",
		},
		placeholderImage: {
			width: "100%",
			height: "100%",
			backgroundColor: theme.colors.systemBackground.secondary,
			justifyContent: "center",
			alignItems: "center",
		},
		details: {
			paddingHorizontal: theme.spacing.xs,
		},
		title: {
			...theme.typography.headline,
			color: theme.colors.text,
			fontWeight: "700",
			marginBottom: theme.spacing.xs,
		},
		subtitle: {
			...theme.typography.subhead,
			color: theme.colors.primary,
			fontWeight: "600",
			marginBottom: theme.spacing.sm,
		},
		description: {
			...theme.typography.body,
			color: theme.colors.textSecondary,
			marginBottom: theme.spacing.md,
			lineHeight: 20,
		},
		stats: {
			flexDirection: "row",
			gap: theme.spacing.md,
		},
		statItem: {
			flexDirection: "row",
			alignItems: "center",
		},
		statIconContainer: {
			width: 28,
			height: 28,
			borderRadius: 14,
			justifyContent: "center",
			alignItems: "center",
		},
		statText: {
			...theme.typography.subhead,
			color: theme.colors.textSecondary,
			fontWeight: "500",
		},
		footer: {
			flexDirection: "row",
			alignItems: "center",
			paddingHorizontal: theme.spacing.md,
			paddingBottom: theme.spacing.sm,
		},
		interactionButton: {
			padding: theme.spacing.xs,
			marginRight: theme.spacing.md,
		},
		actionButton: {
			backgroundColor: theme.colors.primary,
			flexDirection: "row",
			alignItems: "center",
			justifyContent: "center",
			gap: theme.spacing.xs,
			paddingVertical: theme.spacing.xs,
			paddingHorizontal: theme.spacing.md,
			borderRadius: 10,
			marginLeft: "auto",
		},
		actionButtonText: {
			...theme.typography.subhead,
			color: "#fff",
			fontWeight: "600",
		},
	});
};
